import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { Table, Tag, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { frontendDisplayedCourseSelector } from '@/atoms/auth'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import { getNotecardNoteSummaries } from '@/api/cb/contentCreatorContent'
import { NotecardNoteSummary } from 'common/src/api/contentFrontendVsContentBackend/contentCreatorNotes'
import { NotecardPriority } from 'common/src/notecards/consts'
import styles from './ContentCreatorNotes.module.less'
interface NotecardNoteSummaryWithOrder extends NotecardNoteSummary {
    outlineOrder: number
}

export const NotecardNotesTable: React.FC<unknown> = (): ReactElement => {
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const setViewingState = useSetRecoilState(viewingStateAtom)
    const [rawSummaries, setRawSummaries] = useState<NotecardNoteSummary[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!courseName) return
        setLoading(true)
        getNotecardNoteSummaries(courseName).then((response) => {
            if (!response.data.isError) {
                setRawSummaries(response.data.payload.summaries)
            }
            setLoading(false)
        })
    }, [courseName])

    const summaries = useMemo(
        (): NotecardNoteSummaryWithOrder[] =>
            rawSummaries.map((s, i) => ({ ...s, outlineOrder: i })),
        [rawSummaries]
    )

    const navigateToNotecard = useCallback(
        (summary: NotecardNoteSummary): void => {
            setViewingState({
                contentPath: summary.contentPath,
                viewingStateType: ViewingStateType.notecards,
                id: summary.id,
            })
        },
        [setViewingState]
    )

    const allCategories = [
        ...new Set(summaries.flatMap((s) => s.categories)),
    ].sort()

    const columns: ColumnsType<NotecardNoteSummaryWithOrder> = [
        {
            title: 'Section',
            dataIndex: 'sectionName',
            key: 'sectionName',
            width: 200,
            sorter: (a, b) => a.outlineOrder - b.outlineOrder,
        },
        {
            title: 'Importance',
            dataIndex: 'importance',
            key: 'importance',
            width: 110,
            sorter: (a, b) => a.importance.localeCompare(b.importance),
            filters: Object.values(NotecardPriority).map((p) => ({
                text: p,
                value: p,
            })),
            onFilter: (value, record) => record.importance === value,
        },
        {
            title: 'Categories',
            dataIndex: 'categories',
            key: 'categories',
            width: 200,
            render: (categories: string[]) =>
                categories.map((c) => <Tag key={c}>{c}</Tag>),
            filters: allCategories.map((c) => ({ text: c, value: c })),
            onFilter: (value, record) =>
                record.categories.includes(value as string),
        },
        {
            title: 'Prev Exam?',
            dataIndex: 'isPreviousExamQuestion',
            key: 'isPreviousExamQuestion',
            width: 100,
            render: (val: boolean) => (val ? 'Yes' : 'No'),
            filters: [
                { text: 'Yes', value: true },
                { text: 'No', value: false },
            ],
            onFilter: (value, record) =>
                record.isPreviousExamQuestion === value,
        },
        {
            title: 'My Notes',
            dataIndex: 'myNotes',
            key: 'myNotes',
            width: 250,
            ellipsis: true,
            render: (val: string) =>
                val ? (
                    <Tooltip
                        title={
                            <span style={{ whiteSpace: 'pre-wrap' }}>
                                {val}
                            </span>
                        }
                        placement="topLeft"
                        overlayStyle={{ maxWidth: 500 }}
                    >
                        <span className={styles.notesPreview}>{val}</span>
                    </Tooltip>
                ) : (
                    '-'
                ),
        },
        {
            title: 'Link',
            key: 'link',
            width: 60,
            render: (_: unknown, record: NotecardNoteSummary) => (
                <a
                    className={styles.linkButton}
                    onClick={() => navigateToNotecard(record)}
                >
                    View
                </a>
            ),
        },
    ]

    return (
        <div className={styles.contentCreatorNotesTable}>
            <div className={styles.title}>Notecard Notes</div>
            <Table
                columns={columns}
                dataSource={summaries}
                rowKey="id"
                loading={loading}
                size="small"
                pagination={{ pageSize: 50, showSizeChanger: true }}
                scroll={{ x: 1000 }}
            />
        </div>
    )
}
